import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Sun, Moon, User, LogOut, Zap, ChevronDown, Download } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { canAccessFinancialModules, roleLabel } from '../../utils/roles';

export default function Header({ onMenuClick, onNotifClick }) {
  const { t } = useTranslation();
  const { state, actions } = useApp();
  const { user, farm, plan, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Page titles mapped to translation keys
  const PAGE_TITLES = {
    '/': t('navigation.dashboard'),
    '/rooms': t('navigation.rooms'),
    '/rooms/view': t('navigation.roomDetail'),
    '/incubator': t('navigation.incubator'),
    '/expenses': t('navigation.expenses'),
    '/sales': t('navigation.sales'),
    '/employees': t('navigation.employees'),
    '/attendance': t('navigation.attendance'),
    '/payroll': t('navigation.payroll'),
    '/reports': t('navigation.reports'),
    '/notifications': t('navigation.notifications'),
    '/profile': t('navigation.profile'),
    '/upgrade': t('navigation.upgradePlan'),
  };

  const title = PAGE_TITLES[location.pathname] || t('common.appName');

  const notificationCount = (state.notifications ?? []).filter((n) => !n.read).length;

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  // PWA install state
  // installPrompt: the beforeinstallprompt event (if fired) - means app is installable
  // isInstallable: true if beforeinstallprompt fired, false if not (likely already installed)
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(null); // null = unknown, true = installable, false = likely installed
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const initials = (user?.name || 'F').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const isPro = plan === 'pro' || plan === 'growth';  // any paid plan hides the upgrade button
  const canManageBilling = canAccessFinancialModules(user?.role);

  // Check if PWA is installed and detect platform
  useEffect(() => {
    // Detect platform - log for debugging
    const ua = navigator.userAgent;
    console.log('User Agent:', ua);

    // Android detection: look for Android but exclude iOS devices
    // Some Android browsers may have iPhone/iPad in UA for compatibility, so check Android first
    const hasAndroid = /Android/.test(ua);
    const hasIOS = /iPhone|iPad|iPod/.test(ua);
    const android = hasAndroid && !hasIOS;
    const ios = hasIOS;

    console.log('Platform detection:', { android, ios, hasAndroid, hasIOS });
    setIsAndroid(android);
    setIsIOS(ios);

    // Check if running as standalone PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');

    // If already in standalone mode, don't show install option
    if (isStandalone) {
      setIsInstallable(false);
      return;
    }

    // Listen for beforeinstallprompt event (Chrome/Android)
    // If this fires, the app is installable (not yet installed)
    let beforeInstallFired = false;
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt fired:', e);
      beforeInstallFired = true;
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Also try to capture it if it already fired (for late component mount)
    if (window.deferredPrompt) {
      beforeInstallFired = true;
      setInstallPrompt(window.deferredPrompt);
      setIsInstallable(true);
    }

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstallable(false);
      setInstallPrompt(null);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    // If beforeinstallprompt doesn't fire within 2 seconds:
    // - On mobile (Android/iOS), show install option anyway (user can manually add to home screen)
    // - On desktop, assume already installed or not installable
    // Note: Chrome requires HTTPS for beforeinstallprompt, so accessing via IP won't trigger it
    const timeoutId = setTimeout(() => {
      if (!beforeInstallFired) {
        console.log('beforeinstallprompt did not fire');
        const isMobile = android || ios;
        if (isMobile) {
          // On mobile, show install option even without native prompt
          // (user can manually add to home screen via browser menu)
          console.log('Mobile detected - showing manual install option');
          setIsInstallable(true);
        } else {
          console.log('Desktop detected - assuming already installed or not installable');
          setIsInstallable(false);
        }
      }
    }, 2000);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Handle PWA install
  const handleInstallPWA = async () => {
    setMenuOpen(false);

    if (installPrompt) {
      // Android Chrome - has native install prompt
      console.log('Triggering native install prompt');
      installPrompt.prompt();
      const result = await installPrompt.userChoice;
      console.log('Install prompt result:', result);
      if (result.outcome === 'accepted') {
        setIsInstallable(false);
        setInstallPrompt(null);
      }
    } else if (isAndroid) {
      // Android but no native prompt available (might be in WebView, Samsung browser, etc.)
      // Show Android-specific instructions
      setShowInstallModal(true);
    } else if (isIOS) {
      // iOS Safari - show iOS instructions
      setShowInstallModal(true);
    } else {
      // Desktop or other browser - show generic instructions
      setShowInstallModal(true);
    }
  };

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const goTo = (path) => { setMenuOpen(false); navigate(path); };
  const handleLogout = () => { setMenuOpen(false); logout(); navigate('/login', { replace: true }); };

  return (
    <>
      <header className="flex-shrink-0 h-14 lg:h-16 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex items-center pl-2 pr-3 lg:px-5 gap-1 z-20">

        {/* Mobile brand mark */}
        <div className="flex lg:hidden items-center gap-1.5 flex-shrink-0">
          <img src="/logo-header.svg" alt="FarmSmartly" className="h-8 w-auto rounded-lg" />
          <span className="text-sm font-bold text-primary-800 dark:text-primary-300 leading-none">
            FarmSmartly
          </span>
        </div>

        {/* Divider mobile only */}
        <div className="lg:hidden w-px h-5 bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

        {/* Page title */}
        <div className="flex-1 min-w-0">
          <h1 className="text-sm lg:text-lg font-semibold lg:font-bold text-gray-600 dark:text-gray-400 lg:text-gray-900 lg:dark:text-gray-100 truncate">
            {title}
          </h1>
          <p className="hidden lg:block text-xs text-gray-400 dark:text-gray-500 leading-none mt-0.5">
            FarmSmartly
          </p>
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-1">

          {/* Theme toggle */}
          <button
            onClick={actions.toggleTheme}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={t('common.toggleTheme')}
          >
            {state.theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          {/* Notifications */}
          <button
            onClick={onNotifClick}
            className="w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors relative"
            aria-label={t('navigation.notifications')}
          >
            <Bell size={18} />
            {notificationCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-accent-500 text-primary-900 text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                {notificationCount}
              </span>
            )}
          </button>

          {/* ── Profile avatar + dropdown (all screen sizes) ────────────────────── */}
          <div ref={menuRef} className="relative ml-1 lg:ml-2 lg:pl-2 lg:border-l lg:border-gray-200 lg:dark:border-gray-700">

            {/* Trigger */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              aria-label={t('navigation.profile')}
            >
              <div className="w-8 h-8 rounded-full bg-primary-800 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {initials}
              </div>
              <div className="hidden xl:block text-left">
                <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight">{user?.name || t('common.growth')}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">{roleLabel(user?.role)}</p>
              </div>
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform hidden xl:block ${menuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown panel */}
            {menuOpen && (
              <div className="absolute top-full right-0 mt-3 w-60 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden z-50">

                {/* User card header */}
                <div className="bg-gradient-to-br from-primary-800 to-primary-600 px-4 py-4">
                  <p className="text-white font-bold text-sm leading-tight">{user?.name || t('common.growth')}</p>
                  <p className="text-primary-200 text-xs mt-0.5">{user?.email || ''}</p>
                  <span className="mt-2.5 inline-flex items-center gap-1 bg-white/15 text-white text-[10px] font-semibold px-2.5 py-1 rounded-full">
                    <Zap size={10} /> {plan === 'pro' ? t('navigation.proPlan') : plan === 'growth' ? t('navigation.growthPlan') : t('navigation.freePlan')}
                  </span>
                </div>

                {/* Role badge */}
                <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800">
                  <span className="inline-flex items-center bg-primary-800 dark:bg-primary-700 text-white text-[11px] font-bold px-3 py-1 rounded-full tracking-wide">
                    {roleLabel(user?.role).toUpperCase()}
                  </span>
                </div>

                {/* Menu items */}
                <div className="py-1.5">
                  <MenuItem icon={User} label={t('navigation.profile')} onClick={() => goTo('/profile')} />

                  {/* Install PWA option - only show if installable (beforeinstallprompt fired) */}
                  {isInstallable === true && (
                    <MenuItem
                      icon={Download}
                      label={t('navigation.installApp')}
                      onClick={handleInstallPWA}
                      className="text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                    />
                  )}
                </div>

                <div className="border-t border-gray-100 dark:border-gray-800 py-1.5">
                  {/* Upgrade to Pro — only for free plan users */}
                  {!isPro && canManageBilling && (
                    <div className="mx-3 mb-1.5">
                      <button
                        onClick={() => goTo('/upgrade')}
                        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-400 hover:to-orange-300 text-white text-sm font-bold transition-all shadow-sm"
                      >
                        <Zap size={14} /> {t('navigation.upgradeToPro')}
                      </button>
                    </div>
                  )}
                  <MenuItem
                    icon={LogOut} label={t('navigation.signOut')}
                    onClick={handleLogout}
                    className="text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

      </header>

      {/* PWA Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-br from-primary-800 to-primary-600 px-6 py-5">
              <h3 className="text-white font-bold text-lg">{t('navigation.installApp')}</h3>
              <p className="text-primary-200 text-sm mt-1">
                {t('profile.installAppSubtitle')}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <img src="/logo-header.svg" alt="FarmSmartly" className="h-10 w-10 rounded-lg flex-shrink-0" />
                <div>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {t('profile.installAppDescription')}
                  </p>
                </div>
              </div>

              {/* Platform instructions */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4 mb-4">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm mb-3">
                  {t('profile.howToInstall')}
                </h4>

                {isIOS ? (
                  // iOS Instructions
                  <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['installIos1', 'installIos2', 'installIos3'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                        <span dangerouslySetInnerHTML={{ __html: t(`profile.${key}`) }} />
                      </li>
                    ))}
                  </ol>
                ) : isAndroid ? (
                  // Android Instructions (when native prompt not available)
                  <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['installAndroid1', 'installAndroid2', 'installAndroid3'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                        <span dangerouslySetInnerHTML={{ __html: t(`profile.${key}`) }} />
                      </li>
                    ))}
                  </ol>
                ) : (
                  // Desktop Instructions
                  <ol className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    {['installDesktop1', 'installDesktop2', 'installDesktop3'].map((key, i) => (
                      <li key={key} className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                        <span dangerouslySetInnerHTML={{ __html: t(`profile.${key}`) }} />
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowInstallModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    // Try to trigger the native prompt if available now
                    if (window.navigator.standalone === false) {
                      // iOS - can't auto-trigger, just close
                      setShowInstallModal(false);
                    } else if (installPrompt) {
                      installPrompt.prompt();
                      setShowInstallModal(false);
                    } else {
                      setShowInstallModal(false);
                    }
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-500 rounded-xl transition-colors"
                >
                  {t('common.ok')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, className = '' }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 ${className}`}
    >
      <Icon size={15} className="flex-shrink-0" />
      {label}
    </button>
  );
}
